import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchAllProjects } from "../../../store/slices/projectsSlice";
import { fetchLeads } from "../../../store/slices/leadsSlice";
import { createProjectKey } from "../../../utils/projectMatchingUtils";

const useMatchedProject = (operationProjectName, operationProjectQuotationId = null) => {
    const dispatch = useDispatch();

    const { projects } = useSelector((state) => state.projects);
    const { leads } = useSelector((state) => state.leads);
    const { entries } = useSelector((state) => state.workentries);
    const { items } = useSelector((state) => state.hardwareDescription);
    const { employees } = useSelector((state) => state.employees);

    const [projectsReceived, setProjectsReceived] = useState([]);
    const [matchedProject, setMatchedProject] = useState(null);

    // Fetch initial data only once
    useEffect(() => {
        dispatch(fetchAllProjects());
        dispatch(fetchLeads());
    }, [dispatch]);

    // Build grouped + filtered project list
    useEffect(() => {
        if (projects?.length) {

            // REFACTORED: Group by composite key (ProjectName + ClientName + AssignedGroup)
            // UNDO: Change back to grouping by ProjectName only if reverting
            const projectsGroupedByName = projects.reduce((acc, project) => {
                const projectKey = createProjectKey(project);
                const existingGroup = acc.find((g) => {
                    const groupKey = createProjectKey({
                        ProjectName: g.ProjectName,
                        ClientName: project.ClientName,
                        AssignedGroup: g.AssignedGroup || project.AssignedGroup
                    });
                    return groupKey === projectKey;
                });

                if (existingGroup) {
                    existingGroup.Projects.push(project);
                } else {
                    acc.push({
                        ProjectName: project.ProjectName,
                        ClientName: project.ClientName, // REFACTORED: Include for composite matching
                        AssignedGroup: project.AssignedGroup, // REFACTORED: Include for composite matching
                        Projects: [project],
                    });
                }
                return acc;
            }, []);

            // 2️⃣ Filter valid groups
            const qualifiedGroups = projectsGroupedByName.filter((group) =>
                group.Projects.every((project) => {
                    if (!project?.Assignedperson?.length) return false;
                    return true;
                })
            );

            // 3️⃣ Combine with leads
            const groupedProjects = qualifiedGroups.map((group) => {

                const allStatuses = group.Projects.flatMap(p =>
                    p.Assignedperson?.map(person => person.salespersonstatus) || []
                );

                // ⭐ 2️⃣ Count frequency of each status
                const statusCount = allStatuses.reduce((acc, status) => {
                    acc[status] = (acc[status] || 0) + 1;
                    return acc;
                }, {});

                // ⭐ 3️⃣ Select most occurring status (mode)
                let SalesStatus = Object.entries(statusCount).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

                // REFACTORED: Use composite key matching instead of just ProjectName
                // UNDO: Change back to simple ProjectName matching if reverting
                const matchingLead = leads.find((lead) => {
                    const leadKey = createProjectKey(lead);
                    const groupKey = createProjectKey({
                        ProjectName: group.ProjectName,
                        ClientName: lead.ClientName,
                        AssignedGroup: group.AssignedGroup || lead.AssignedGroup
                    });
                    return leadKey === groupKey;
                });

                // Check if manager has filled in data (for ERP package wizard)
                // If lead status is "Pending" and manager has work entries, set status to "response received"
                const leadStatus = matchingLead?.Status?.toLowerCase()?.trim();
                const isPending = leadStatus === "pending";
                
                if (isPending && employees?.length > 0 && (entries?.length > 0 || items?.length > 0)) {
                    // Check if any manager has filled in data for any project in the group
                    const hasManagerData = group.Projects.some((project) => {
                        const managerEmail = project.AssignedManagerEmail?.[0];
                        const manager = employees.find((emp) => emp.EmpEmail === managerEmail);
                        const managerId = manager?._id;
                        
                        if (!managerId) return false;
                        
                        // Check if manager has work entries or hardware items
                        const hasEntries = entries.some(
                            (entry) => entry.projectid === project._id && entry.taskownerid === managerId
                        );
                        const hasItems = items.some(
                            (item) => item.projectid === project._id && item.taskownerid === managerId
                        );
                        
                        return hasEntries || hasItems;
                    });
                    
                    // If manager has filled data, set status to "response received"
                    if (hasManagerData) {
                        SalesStatus = "response received";
                    }
                }

                return {
                    quotationId: group.Projects[0]?.quotationId || null,
                    isRead: matchingLead?.isRead || false,
                    AssignedGroup: group.Projects[0].AssignedGroup,
                    ProjectName: group.ProjectName,
                    ClientName: group.ClientName || group.Projects[0]?.ClientName || '',
                    DateRequested: group.Projects[0].DateRequested,
                    ProjectDueDate: group.Projects[0].ProjectDueDate,
                    Projects: group.Projects,
                    SalesStatus: SalesStatus
                };
            });
            setProjectsReceived(groupedProjects);
        }
    }, [projects, leads, entries, items, employees]);

    useEffect(() => {
        if (!operationProjectName && operationProjectQuotationId == null) return;
        if (!projectsReceived?.length) return;

        const found = projectsReceived.find((proj) =>
            (operationProjectQuotationId != null && proj.quotationId != null)
                ? String(proj.quotationId) === String(operationProjectQuotationId)
                : proj.ProjectName === operationProjectName
        );

        setMatchedProject(found || null);
    }, [operationProjectName, operationProjectQuotationId, projectsReceived]);


    // return matchedProject;
    return {
        matchedProject: matchedProject ? [matchedProject] : [],
        SalesStatus: matchedProject?.SalesStatus || null
    };

};

export default useMatchedProject;
